import React from 'react';
import { Alert, Box, Grid, Stack, Typography } from '@mui/material';

import BudgetVersionCardsPanel from './BudgetVersionCardsPanel';
import BudgetVersionFilterBar from './BudgetVersionFilterBar';
import BudgetVersionGrid from './BudgetVersionGrid';
import BudgetVersionLinesGrid from './BudgetVersionLinesGrid';

import {
  adoptBudgetVersion,
  createBudgetRevision,
  defaultApprovalStages,
  defaultVersionTypes,
  fetchBudgetVersionCards,
  fetchBudgetVersionLines,
  fetchBudgetVersions,
  freezeBudgetForDG,
  submitBudgetVersionToCA,
} from './budgetVersionApi';

type State = {
  fiscalYear: string;
  versionType: string;
  approvalStage: string;
  governancePolicyCode: string;
  notes: string;
  loading: boolean;
  error: string;
  success: string;
  cards: any;
  versions: any[];
  selected: any | null;
  lines: any[];
};

class BudgetVersionWorkbench extends React.Component<any, State> {
  state: State = {
    fiscalYear: '2026',
    versionType: '',
    approvalStage: '',
    governancePolicyCode: 'ACCELERATED_APPROVAL',
    notes: '',
    loading: false,
    error: '',
    success: '',
    cards: {},
    versions: [],
    selected: null,
    lines: [],
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    const { fiscalYear, versionType, approvalStage } = this.state;
    this.setState({ loading: true, error: '', success: '' });

    try {
      const [cards, versions] = await Promise.all([
        fetchBudgetVersionCards(),
        fetchBudgetVersions({
          fyr: fiscalYear || null,
          vty: versionType || null,
          aps: approvalStage || null,
        }),
      ]);

      const selected = versions.length ? versions[0] : null;
      const lines = selected ? await fetchBudgetVersionLines(selected.rid) : [];

      this.setState({ cards, versions, selected, lines });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  selectVersion = async (selected: any) => {
    this.setState({ selected, loading: true, error: '', success: '' });

    try {
      const lines = await fetchBudgetVersionLines(selected.rid);
      this.setState({ lines });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  createRevision = async () => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      await createBudgetRevision({
        fiscalYear: this.state.fiscalYear,
        versionType: this.state.versionType || 'DRAFT',
        notes: this.state.notes,
      });
      this.setState({ success: 'Budget revision created.' });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  freezeDG = async () => {
    if (!this.state.selected) return;
    this.setState({ loading: true, error: '', success: '' });

    try {
      await freezeBudgetForDG(this.state.selected.rid, this.state.notes);
      this.setState({ success: 'Budget version frozen for DG review.' });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  submitCA = async () => {
    if (!this.state.selected) return;
    this.setState({ loading: true, error: '', success: '' });

    try {
      await submitBudgetVersionToCA(this.state.selected.rid, this.state.governancePolicyCode, this.state.notes);
      this.setState({ success: 'Budget version submitted to CA.' });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  adopt = async () => {
    if (!this.state.selected) return;
    this.setState({ loading: true, error: '', success: '' });

    try {
      await adoptBudgetVersion(this.state.selected.rid, this.state.notes);
      this.setState({ success: 'Budget version adopted.' });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const s = this.state;

    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Budget Versioning Workbench
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Freeze validated budget data into controlled versions for DG review, CA submission and final adoption.
            </Typography>
          </Box>

          <BudgetVersionFilterBar
            fiscalYear={s.fiscalYear}
            versionType={s.versionType}
            approvalStage={s.approvalStage}
            governancePolicyCode={s.governancePolicyCode}
            notes={s.notes}
            versionTypes={defaultVersionTypes}
            approvalStages={defaultApprovalStages}
            selectedVersion={s.selected}
            loading={s.loading}
            onFiscalYearChange={(fiscalYear) => this.setState({ fiscalYear })}
            onVersionTypeChange={(versionType) => this.setState({ versionType })}
            onApprovalStageChange={(approvalStage) => this.setState({ approvalStage })}
            onGovernancePolicyChange={(governancePolicyCode) => this.setState({ governancePolicyCode })}
            onNotesChange={(notes) => this.setState({ notes })}
            onRefresh={this.load}
            onCreateRevision={this.createRevision}
            onFreezeDG={this.freezeDG}
            onSubmitCA={this.submitCA}
            onAdopt={this.adopt}
          />

          {s.error && <Alert severity="error">{s.error}</Alert>}
          {s.success && <Alert severity="success">{s.success}</Alert>}

          <BudgetVersionCardsPanel cards={s.cards} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <BudgetVersionGrid
                rows={s.versions}
                selectedId={s.selected?.rid}
                loading={s.loading}
                onSelect={this.selectVersion}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <BudgetVersionLinesGrid rows={s.lines} loading={s.loading} />
            </Grid>
          </Grid>
        </Stack>
      </Box>
    );
  }
}

export default BudgetVersionWorkbench;